import {
	createTheme,
	MenuProps,
	Select,
	SelectProps,
	SvgIcon,
	SvgIconProps,
	ThemeOptions,
	ThemeProvider,
} from "@mui/material";
import { ReactNode, useMemo, useRef } from "react";
import CaretDown from "./images/caret-down.svg?react";
import CoreTextField, { CoreTextFieldProps } from "../form/TextField";
import { themeOptions } from "../../theme";
import { deepmerge } from "@mui/utils";
import { FormControlTooltip, FormControlTooltipProps } from "../tooltip";
import DropdownSearchHeader, { CoreSelectSearchProps } from "./DropdownSearch";

function DropdownIcon({ ...props }: SvgIconProps) {
	return (
		<SvgIcon
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			sx={{
				"&.MuiSvgIcon-root": {
					width: 17,
					height: 17,
					top: "calc(50% - 8.5px)",
					color: "#4D4D4D",
				},
			}}
		>
			<CaretDown />
		</SvgIcon>
	);
}

const selectThemeOptions: ThemeOptions = {
	components: {
		MuiSelect: {
			defaultProps: {
				IconComponent: DropdownIcon,
			},
		},
	},
};

type SharedSelectPropsOptions = {
	searchProps?: CoreSelectSearchProps;
	shadowDomContainer?: HTMLElement | null;
	menuProps?: Partial<MenuProps>;
	handleShadowDomArrowKeys?: (event: React.KeyboardEvent, container: HTMLElement) => void;
};

function getSharedSelectMenuProps({
	searchProps,
	shadowDomContainer,
	menuProps = {},
	handleShadowDomArrowKeys,
}: SharedSelectPropsOptions): Partial<MenuProps> {
	const { onKeyDown: consumerOnKeyDown, TransitionProps: consumerTransitionProps, ...remainingMenuProps } = menuProps;

	const baseMenuProps: Partial<MenuProps> = {
		// If searching, turn off native autoFocus so our text input grabs it instead
		autoFocus: searchProps ? false : menuProps.autoFocus,
		container: shadowDomContainer,
		onKeyDown: (e) => {
			if (consumerOnKeyDown) {
				consumerOnKeyDown(e);
			}
			if (shadowDomContainer && handleShadowDomArrowKeys) {
				handleShadowDomArrowKeys(e, shadowDomContainer);
			}
		},
		TransitionProps: {
			...consumerTransitionProps,
			onExited: (node) => {
				if (consumerTransitionProps?.onExited) {
					consumerTransitionProps.onExited(node);
				}
				searchProps?.onChange("");
			},
		},
	};

	// Deep merge with any specific menu props passed down by the unique component instance
	return deepmerge(baseMenuProps, remainingMenuProps);
}

function handleShadowDomArrowKeys(event: React.KeyboardEvent, container: HTMLElement) {
	if (!container) return;

	const validKeys = ["ArrowUp", "ArrowDown"];
	if (!validKeys.includes(event.key)) return;

	const listBox = container.querySelector(".MuiMenu-root ul[role='listbox']");
	if (!listBox) return;

	const menuItems = Array.from(listBox.querySelectorAll("li[role='option']"));
	if (menuItems.length === 0) return;

	const rootNode = event.currentTarget.getRootNode();
	const focused = rootNode instanceof ShadowRoot ? rootNode.activeElement : document.activeElement;

	let currentIndex = menuItems.indexOf(focused as Element);

	if (currentIndex === -1) {
		const selected = listBox.querySelector("li.Mui-selected");
		currentIndex = selected ? menuItems.indexOf(selected) : 0;
	}

	let nextIndex = currentIndex;
	if (event.key === "ArrowDown") {
		nextIndex = Math.min(currentIndex + 1, menuItems.length - 1);
	} else if (event.key === "ArrowUp") {
		nextIndex = Math.max(currentIndex - 1, 0);
	}

	if (nextIndex !== currentIndex) {
		event.preventDefault();
		(menuItems[nextIndex] as HTMLElement).focus();
	}
}

export type CoreSelectProps<Value> = Omit<SelectProps<Value>, "inputRef"> & {
	label?: string;
	renderValue?: (value: Value) => ReactNode;
	shadowDomContainer?: HTMLElement | null /**Keyboard breaks in shadowdom */;
	searchProps?: CoreSelectSearchProps;
} & Pick<FormControlTooltipProps<false>, "tooltip" | "disabledTooltip" | "slotProps">;

export default function CoreSelect<Value>({
	label,
	renderValue,
	shadowDomContainer,
	tooltip,
	disabledTooltip,
	"aria-label": ariaLabelProp,
	slotProps,
	searchProps,
	children,
	...selectProps
}: CoreSelectProps<Value>) {
	const selectRef = useRef<HTMLElement>(null);
	const theme = useMemo(() => createTheme(themeOptions, selectThemeOptions), []);
	const ariaLabel = ariaLabelProp ?? label;

	const sharedMenuProps = useMemo(
		() =>
			getSharedSelectMenuProps({
				searchProps,
				shadowDomContainer,
				menuProps: selectProps.MenuProps,
				handleShadowDomArrowKeys,
			}),
		[searchProps, shadowDomContainer, selectProps.MenuProps],
	);

	return (
		<ThemeProvider theme={theme}>
			<FormControlTooltip tooltip={tooltip} disabledTooltip={disabledTooltip} slotProps={slotProps}>
				<Select
					{...selectProps}
					aria-label={ariaLabel}
					inputRef={selectRef}
					{...(renderValue && {
						renderValue: (value: unknown) => (value !== null ? renderValue(value as Value) : label),
					})}
					MenuProps={sharedMenuProps}
					SelectDisplayProps={{
						"aria-label": ariaLabel,
						...selectProps.SelectDisplayProps,
					}}
					inputProps={{
						"aria-label": ariaLabel,
						...selectProps.inputProps,
					}}
				>
					{searchProps && <DropdownSearchHeader searchProps={searchProps} />}
					{children}
				</Select>
			</FormControlTooltip>
		</ThemeProvider>
	);
}

export type CoreSelectTextFieldProps<Value> = Omit<CoreTextFieldProps, "select" | "error" | "inputRef"> & {
	label?: string;
	ariaLabelledBy?: string;
	useFloatingLabel?: boolean;
	SelectProps?: Omit<SelectProps<Value>, "renderValue">;
	renderValue: (value: Value) => ReactNode;
	error?: ReactNode;
	shadowDomContainer?: HTMLElement | null /**Keyboard breaks in shadowdom */;
	popperMaxHeight?: string;
	searchProps?: CoreSelectSearchProps;
};

export function CoreSelectTextField<Value>({
	label,
	ariaLabelledBy,
	useFloatingLabel,
	SelectProps,
	renderValue,
	shadowDomContainer,
	popperMaxHeight,
	searchProps,
	children,
	...textFieldProps
}: CoreSelectTextFieldProps<Value>) {
	const selectRef = useRef<HTMLElement>(null);

	const theme = useMemo(() => createTheme(themeOptions, selectThemeOptions), []);

	const baseMenuProps = useMemo(
		() => ({
			transformOrigin: { vertical: -5, horizontal: "center" } as const,
			slotProps: {
				paper: { sx: { maxHeight: popperMaxHeight } },
			},
		}),
		[popperMaxHeight],
	);

	// Build the shared menu configurations
	const sharedMenuProps = useMemo(() => {
		// Deepmerge component baseline with consumer overrides before sharing
		const menuProps = deepmerge(baseMenuProps, SelectProps?.MenuProps ?? {});

		return getSharedSelectMenuProps({
			searchProps,
			shadowDomContainer,
			menuProps,
			handleShadowDomArrowKeys,
		});
	}, [searchProps, shadowDomContainer, SelectProps?.MenuProps, baseMenuProps]);

	return (
		<ThemeProvider theme={theme}>
			<CoreTextField
				{...textFieldProps}
				inputRef={selectRef}
				select
				{...(useFloatingLabel ? { label, useFloatingLabel: true } : {})}
				SelectProps={{
					...SelectProps,
					renderValue: (value) => (value != null ? renderValue(value as Value) : !useFloatingLabel ? label : undefined),

					MenuProps: sharedMenuProps,
					SelectDisplayProps: {
						"aria-label": label,
						"aria-labelledby": ariaLabelledBy,
						...SelectProps?.SelectDisplayProps,
					},
				}}
			>
				{searchProps && <DropdownSearchHeader searchProps={searchProps} />}
				{children}
			</CoreTextField>
		</ThemeProvider>
	);
}
